<template>
	<!-- eslint-disable max-lines-->
	<SiteScannerLayout>
		<template #header-title>
			<span>
				Site Scanner
			</span>
		</template>
		<template #content>
			<FilterBar
				:search-query.sync="siteScannerSearchQuery"
				:selected-brand-id.sync="selectedAdminPortalBrandId"
				search-query-label="Scan Name"
				@persistSearchQuery="changeSiteScannerSearchQuery"
				@persistSelectedBrandId="changeAdminPortalBrandFilter"
				@clearFilters="clearFilters"
			>
				<template #after-filters>
					<TextField
						v-model="siteScannerDomainSearchQuery"
						label="Domain"
						@input="changeSiteScannerDomainSearchQuery($event)"
					/>
				</template>
				<template #action>
					<PrimaryActionButton
						v-if="siteScannerFullAccess"
						@click="scanSite"
					>
						<v-icon left>
							mdi-plus
						</v-icon>
						Scan your domain
					</PrimaryActionButton>
				</template>
			</FilterBar>
			<v-alert
				v-if="siteScannerModuleEnabled"
				v-model="showNewScannerBanner"
				prominent
				color="primary"
				border="left"
				colored-border
				elevation="2"
				class="mb-4 new-scanner-banner"
				:icon="false"
			>
				<v-row align="center">
					<v-col class="shrink ml-3">
						<v-icon
							size="24"
							color="primary"
						>
							mdi-information
						</v-icon>
					</v-col>
					<v-col class="grow pl-2">
						<div class="text-subtitle-2">
							Our new Site Scanner is in beta — try it out while continuing to use the existing version.
						</div>
					</v-col>
					<v-col class="shrink">
						<v-btn
							color="primary"
							small
							class="text-none white--text"
							:disabled="newScannerLoading"
							@click="navigateToNewSiteScanner"
						>
							Try it out
							<v-progress-circular
								v-if="newScannerLoading"
								indeterminate
								size="16"
								class="ml-2"
							/>
						</v-btn>
					</v-col>
				</v-row>
			</v-alert>
			<SectionCard>
				<template #title>
					Cookie Scans
				</template>
				<template #body>
					<DataTable
						:headers="singleScanTableHeaders"
						:items="singleScanData"
						sort-by="id"
						sort-desc
						:loading="isLoading"
						@click:row="viewReport"
					>
						<template #item.id="{item}">
							{{ item.id === 'No Number' ? null : item.id }}
						</template>
						<template #item.brandName="{item}">
							{{ getBrandName(item.brandId) }}
						</template>
						<template #item.dateCompleted="{item}">
							{{ item.status === 'Ready' ? item.dateCompleted : null }}
						</template>
						<template
							#item.status="{item}"
						>
							<div>
								{{ item.status }}
							</div>
						</template>
						<template #item.action="{item}">
							<v-menu
								offset-y
								left
							>
								<template #activator="{ on, attrs }">
									<v-btn
										icon
										small
										v-bind="attrs"
										v-on="on"
										@click.stop
									>
										<v-icon>
											mdi-dots-horizontal
										</v-icon>
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item
										v-if="statusReadyUserCanUse(item)"
										@click="viewReport(item)"
									>
										<v-list-item-content>
											<v-list-item-title>
												View Report
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="item.status.includes('Unsuccessful') && userCanUseSiteScanner"
										@click="viewErrorScanDetails(item)"
									>
										<v-list-item-content>
											<v-list-item-title>
												View Scan Errors
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="item.scanGuid && siteScannerFullAccess"
										@click="handleRunAgainClick(item.scanGuid)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Run Again
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="!item.scanGuid && item.scheduleScanId && siteScannerFullAccess"
										@click="handleCloneScheduledScanClick(item.scheduleScanId)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Run Again
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="statusReadyUserCanUse(item)"
										@click="downloadReport(item.scanGuid, downloadTypeEnum.PDF)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Download Report
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="statusReadyUserCanUse(item) && hasMultipleDomainEntries(item)"
										@click="handleCompareClick(item)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Compare Scan Reports
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="showDownloadButton(item, downloadTypeEnum.COOKIE_REPORT)"
										@click="downloadCsv(item.scanGuid, downloadTypeEnum.COOKIE_REPORT)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Download Cookies
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="showDownloadButton(item, downloadTypeEnum.ADVANCED_TRACKERS)"
										@click="downloadCsv(item.scanGuid, downloadTypeEnum.ADVANCED_TRACKERS)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Download Advanced Trackers
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="showDownloadButton(item, downloadTypeEnum.SUB_RESOURCES)"
										@click="downloadCsv(item.scanGuid, downloadTypeEnum.SUB_RESOURCES)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Download Sub Resources
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="!item.isArchived && siteScannerFullAccess && item.status !== 'Scanning'"
										@click="handleRemovedItemClick(item)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Delete
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
					</DataTable>
				</template>
			</SectionCard>
			<SectionCard>
				<template #title>
					Recurring Scans
				</template>
				<template #body>
					<v-expansion-panels
						v-if="groupedRecurringScans.length > 0"
						:value="0"
					>
						<v-expansion-panel
							v-for="(group, index) in groupedRecurringScans"
							:key="index"
						>
							<v-expansion-panel-header class="custom-header">
								<div class="heading-texts">
									<div class="heading-scan-name">
										<strong>
											{{ group[0].scanName }}
										</strong>
									</div>
									<div>
										{{ headingStatusText(group[0]) }}
									</div>
								</div>
								<template #actions>
									<IconButton
										v-if="siteScannerFullAccess"
										tooltip-text="Clone Recurring Scan"
										@click.stop.prevent="handleCloneScheduledScanClick(group[0].scheduleScanId)"
									>
										mdi-content-duplicate
									</IconButton>
									<IconButton
										v-if="userCanUseSiteScanner && group[0].statusId === 3"
										tooltip-text="Cancel Recurring Scan"
										@click.stop.prevent="() => (recurringScanToCancel = group.find(m => m.status === 'Scheduled'))"
									>
										mdi-cancel
									</IconButton>
									<v-icon>
										mdi-chevron-down
									</v-icon>
								</template>
							</v-expansion-panel-header>
							<v-expansion-panel-content>
								<DataTable
									:headers="recurringScanTableHeaders"
									:items="group"
									sort-by="id"
									sort-desc
									:loading="isLoading"
									@click:row="viewReport"
								>
									<template #item.dateCompleted="{item}">
										{{ item.status === 'Ready' ? item.dateCompleted : null }}
									</template>
									<template
										#item.status="{item}"
									>
										<div
											v-if="item.status ==='Scanning'"
										>
											{{ item.status }}
											<v-progress-circular
												v-if="item.statusDetails && item.statusDetails.percentageComplete"
												:size="20"
												:value="item.statusDetails.percentageComplete"
												:rotate="270"
											/>
										</div>
										<div v-else>
											{{ item.status }}
										</div>
									</template>
									<template #item.action="{item}">
										<v-menu
											offset-y
											left
										>
											<template #activator="{ on, attrs }">
												<v-btn
													icon
													small
													v-bind="attrs"
													v-on="on"
													@click.stop
												>
													<v-icon>
														mdi-dots-horizontal
													</v-icon>
												</v-btn>
											</template>
											<v-list dense>
												<v-list-item
													v-if="statusReadyUserCanUse(item)"
													@click="viewReport(item)"
												>
													<v-list-item-content>
														<v-list-item-title>
															View Report
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item
													v-if="item.status.includes('Unsuccessful') && userCanUseSiteScanner"
													@click="viewErrorScanDetails(item)"
												>
													<v-list-item-content>
														<v-list-item-title>
															View Scan Errors
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item
													v-if="item.status === 'Scanning'"
													@click="handleStatusClick(item)"
												>
													<v-list-item-content>
														<v-list-item-title>
															View Scan Progress
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item
													v-if="statusReadyUserCanUse(item)"
													@click="downloadReport(item.scanGuid, downloadTypeEnum.PDF)"
												>
													<v-list-item-content>
														<v-list-item-title>
															Download Report
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item
													v-if="statusReadyUserCanUse(item) && group.length > 1"
													@click="handleCompareClick(item)"
												>
													<v-list-item-content>
														<v-list-item-title>
															Compare Scan Reports
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item
													v-if="statusReadyUserCanUse(item) && item.cookiesCsvExists"
													@click="downloadCsv(item.scanGuid, downloadTypeEnum.COOKIE_REPORT)"
												>
													<v-list-item-content>
														<v-list-item-title>
															Download Cookies
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item
													v-if="statusReadyUserCanUse(item) && item.trackersCsvExists"
													@click="downloadCsv(item.scanGuid, downloadTypeEnum.ADVANCED_TRACKERS)"
												>
													<v-list-item-content>
														<v-list-item-title>
															Download Advanced Trackers
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item
													v-if="statusReadyUserCanUse(item) && item.resourcesCsvExists"
													@click="downloadCsv(item.scanGuid, downloadTypeEnum.SUB_RESOURCES)"
												>
													<v-list-item-content>
														<v-list-item-title>
															Download Sub Resources
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
												<v-list-item
													v-if="!item.isArchived && siteScannerFullAccess && item.status !== 'Scanning' && item.status !== 'Scheduled'"
													@click="() => (cookieScanToBeRemoved = item)"
												>
													<v-list-item-content>
														<v-list-item-title>
															Delete
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
											</v-list>
										</v-menu>
									</template>
								</DataTable>
							</v-expansion-panel-content>
						</v-expansion-panel>
					</v-expansion-panels>
					<div
						v-else
						class="text-center"
					>
						There are currently no recurring scans scheduled
					</div>
				</template>
			</SectionCard>
			<ScanStatusModal
				v-if="scanStatusModalOpen"
				:scan-details="selectedScanStatus"
				@close="handleCloseStatusModal"
			/>
			<ConfirmDeleteModal
				v-if="cookieScanToBeRemoved"
				:entity-name="cookieScanToBeRemoved.scanName"
				entity-type="Cookie Scan"
				@close="cookieScanToBeRemoved = null"
				@delete="deleteCookieScan"
			/>
			<ConfirmDeleteModal
				v-if="cookieScheduledScanToBeRemoved"
				:entity-name="cookieScheduledScanToBeRemoved.scanName"
				entity-type="Cookie Scan"
				@close="cookieScheduledScanToBeRemoved = null"
				@delete="deleteScheduledCookieScan"
			/>
			<ConfirmDeleteModal
				v-if="recurringScanToCancel"
				:entity-name="recurringScanToCancel.scanName"
				entity-type="Recurring Cookie Scan"
				is-cancel
				@close="recurringScanToCancel = null"
				@delete="cancelRecurringScan"
			/>
			<CompareScansSelectModal
				v-if="compareScansSelectModalOpen"
				:selected-scan="selectedScanToCompare"
				:comparable-scans="comparableScans"
				@close="handleCloseCompareSelectModal"
				@compare="viewComparisonResults"
			/>
			<CompareScansResultsModal
				v-if="compareScansResultsModalOpen"
				:scan-to-compare-from="scanToCompareFrom"
				:scan-to-compare-to="scanToCompareTo"
				@close="handleCloseCompareResultsModal"
			/>
			<SiteScannerUnsuccessfulScanModal
				v-if="showUnsuccessfulScanModal"
				:selected-scan="selectedUnsuccessfulScan"
				@close="closeUnsuccessfulScanModal"
			/>
			<SiteScannerScanTypeModal
				v-if="showScanTypeModal && siteScannerFullAccess"
				:brand-options="brandOptions"
				:client-multi-branded="clientMultiBranded"
				:single-brand="singleBrand"
				:default-brand-in-select="defaultBrandInSelect"
				:user-agents="lookupData.userAgents"
				@close="showScanTypeModal = false"
				@full-scan="handleFullScanSelected"
				@handle-quick-scan-requested="handleQuickScanRequested"
			/>
		</template>
	</SiteScannerLayout>
</template>
<script>
/* eslint-disable max-lines */
import { mapGetters } from 'vuex'
import { format } from 'date-fns'
import SiteScannerLayout from './site-scanner-layout.vue'
import SectionCard from '../../../../../../shared/components/section-card.vue'
import FilterBar from '../../../../../../shared/components/filter-bar.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import IconButton from '../../../../../../shared/components/icon-button.vue'
import downloadCSVMixin from '../../../../../../shared/mixins/download-csv.js'
import generatePDFMixin from '../../../../../../shared/mixins/generate-pdf.js'
import ScanStatusModal from '../../../../../../shared/components/scan-status-modal.vue'
import CompareScansSelectModal from './site-scanner-comparison-select-scans-modal.vue'
import CompareScansResultsModal from './site-scanner-comparison-results-modal.vue'
import SiteScannerUnsuccessfulScanModal from './site-scanner-unsuccessful-scan-modal.vue'
import SiteScannerScanTypeModal from './site-scanner-scan-type-modal.vue'
import ConfirmDeleteModal from '../../../../../../shared/components/confirm-delete-modal.vue'
import { SITE_SCANNER_FULL_ACCESS, SITE_SCANNER_READ_ONLY } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { siteScannerVersion, siteScannerModuleEnabled } from '../../../../../../shared/state/config-keys.js'
import { getSiteScans, downloadScanFile, deleteCookieScan, deleteScheduledCookieScan, cancelRecurringScan, getScanDetails } from '../../../../../../shared/utils/api/site-scanner.js'
import { downloadTypeEnum } from './download-type-enums.js'
import {
	changeSiteScannerSearchQuery,
	changeSiteScannerDomainSearchQuery,
	siteScannerSearchQuery,
	siteScannerDomainSearchQuery
} from '../../../../../../shared/state/site-scanner.js'
import { changeAdminPortalBrandFilter, selectedAdminPortalBrandId, waitForLoad, allBrands, clientMultiBranded, brandOptions, singleBrand, defaultBrandInSelect } from '../../../../../../shared/state/brands.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { DATE_TIME_FORMAT_NO_SECONDS } from '../../../../../../shared/utils/date-formatting.js'
import { SITE_SCANNER_REQUEST_SCAN, SITE_SCANNER_RESULT } from '../../../../router/route-names.js'
import { createExchangeTokenRedirect } from '../../../../services/exchangeTokenService.js'
import { TARGET_APPS } from '../../../../../../shared/enums/target-apps.js'
import { useLabels } from '../../../../../../shared/state/admin-portal-navigation.js'

export default {
	components: {
		SiteScannerLayout,
		SectionCard,
		FilterBar,
		TextField,
		DataTable,
		PrimaryActionButton,
		IconButton,
		ScanStatusModal,
		ConfirmDeleteModal,
		CompareScansSelectModal,
		CompareScansResultsModal,
		SiteScannerUnsuccessfulScanModal,
		SiteScannerScanTypeModal
	},
	mixins: [downloadCSVMixin, generatePDFMixin],
	setup () {
		const brandLabel = useLabels('Brand')
		return {
			downloadTypeEnum,
			siteScannerVersion,
			siteScannerModuleEnabled,
			changeSiteScannerSearchQuery,
			changeSiteScannerDomainSearchQuery,
			siteScannerSearchQuery,
			siteScannerDomainSearchQuery,
			changeAdminPortalBrandFilter,
			selectedAdminPortalBrandId,
			allBrands,
			brandLabel,
			clientMultiBranded,
			brandOptions,
			singleBrand,
			defaultBrandInSelect
		}
	},
	data () {
		return {
			singleScans: [],
			recurringScans: [],
			lookupData: null,
			scanStatusModalOpen: false,
			selectedScanStatus: null,
			selectedScanToCompare: null,
			isLoading: false,
			cookieScanToBeRemoved: null,
			cookieScheduledScanToBeRemoved: null,
			recurringScanToCancel: null,
			compareScansSelectModalOpen: false,
			compareScansResultsModalOpen: false,
			scanToCompareFrom: null,
			scanToCompareTo: null,
			showUnsuccessfulScanModal: false,
			selectedUnsuccessfulScan: null,
			showScanTypeModal: false,
			showNewScannerBanner: true,
			newScannerLoading: false
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		singleScanTableHeaders () {
			let tableHeaders = [
				{ text: 'ID', value: 'id', width: '8%' },
				{ text: this.brandLabel, value: 'brandName', width: '12%' },
				{ text: 'Domain', value: 'domain', width: '25%' },
				{ text: 'Date Requested', value: 'dateRequested', width: '20%' },
				{ text: 'Date Complete', value: 'dateCompleted', width: '20%' },
				{ text: 'Status', value: 'status', width: '15%' },
				{ text: 'Action', value: 'action', width: '80px', align: 'center', sortable: false }
			]

			if (this.siteScannerV2) {
				tableHeaders = [
					{ text: 'ID', value: 'id', width: '6%' },
					{ text: this.brandLabel, value: 'brandName', width: '10%' },
					{ text: 'Scan Name', value: 'scanName', width: '20%' },
					{ text: 'Domain', value: 'domain', width: '18%' },
					{ text: 'Date Requested', value: 'dateRequested', width: '15%' },
					{ text: 'Date Complete', value: 'dateCompleted', width: '15%' },
					{ text: 'Status', value: 'status', width: '16%' },
					{ text: 'Action', value: 'action', width: '80px', align: 'center', sortable: false }
				]
			}
			return tableHeaders
		},
		recurringScanTableHeaders () {
			const tableHeaders = [
				{ text: 'ID', value: 'id', width: '8%' },
				{ text: this.brandLabel, value: 'brandName', width: '12%' },
				{ text: 'Scan Name', value: 'scanName', width: '22%' },
				{ text: 'Domain', value: 'domain', width: '22%' },
				{ text: 'Date Requested', value: 'dateRequested', width: '12%' },
				{ text: 'Date Complete', value: 'dateCompleted', width: '12%' },
				{ text: 'Status', value: 'status', width: '12%' },
				{ text: 'Action', value: 'action', width: '80px', align: 'center', sortable: false }
			]
			return tableHeaders
		},
		singleScanData () {
			return this.filterScans(this.singleScans)
		},
		recurringScanData () {
			return this.filterScans(this.recurringScans)
		},
		groupedRecurringScans () {
			const groups = {}
			this.recurringScanData?.forEach(item => {
				const groupId = item.groupedSchedulesID
				if (groupId !== 0) {
					if (!groups[groupId]) {
						groups[groupId] = []
					}
					groups[groupId].push(item)
				}
			})
			this.recurringScanData?.forEach(item => {
				const groupId = item.groupedSchedulesID
				if (groupId === 0) {
					groups[item.id] = [item]
				}
			})
			const groupsArray = Object.values(groups)
			groupsArray.forEach(group => {
				group.sort((a, b) => {
					return new Date(b.dateRequestedUnformatted) - new Date(a.dateRequestedUnformatted)
				})
			})
			return groupsArray
		},
		userCanUseSiteScanner () {
			return this.productAreaPermission(SITE_SCANNER_READ_ONLY)
		},
		siteScannerFullAccess () {
			return this.productAreaPermission(SITE_SCANNER_FULL_ACCESS)
		},
		siteScannerV2 () {
			const siteScannerVersion2 = 2
			return siteScannerVersion.value === siteScannerVersion2
		}
	},
	async created () {
		await waitForLoad()
		await this.getSiteScans()
	},
	methods: {
		statusReadyUserCanUse (item) {
			return item.status === 'Ready' && this.userCanUseSiteScanner
		},
		showDownloadButton (item, downloadType) {
			const canShow = item.isAggregate && this.statusReadyUserCanUse(item)
			switch (downloadType) {
				case downloadTypeEnum.COOKIE_REPORT:
					return canShow && item.cookiesCsvExists
				case downloadTypeEnum.ADVANCED_TRACKERS:
					return canShow && item.trackersCsvExists
				case downloadTypeEnum.SUB_RESOURCES:
					return canShow && item.resourcesCsvExists
				default:
					return false
			}
		},
		async getSiteScans () {
			this.isLoading = true
			const { data: { singleScans, recurringScans, lookupData } } = await getSiteScans()
			this.isLoading = false
			this.singleScans = singleScans
			this.recurringScans = recurringScans
			this.lookupData = lookupData
		},
		getBrandName (brandId) {
			const brand = allBrands.value.find(brand => brand.brandId === brandId)
			return brand?.text
		},
		scanSite () {
			this.showScanTypeModal = true
		},
		handleFullScanSelected () {
			this.showScanTypeModal = false
			this.$router.push({
				name: SITE_SCANNER_REQUEST_SCAN,
				params: {
					scanTypes: this.lookupData.scanTypes,
					maxUrls: this.lookupData.maxScanUrlResults,
					actionTypes: this.lookupData.actionTypes,
					loginTemplates: this.lookupData.loginTemplates,
					userAgents: this.lookupData.userAgents
				}
			})
		},
		handleQuickScanSelected () {
			this.showScanTypeModal = false
		},
		async handleRunAgainClick (scanGuid) {
			const getScanDetailsResponse = await getScanDetails(scanGuid, null)
			const noUserAgent = !getScanDetailsResponse?.data.userAgent || getScanDetailsResponse?.data.userAgent === 0
			if (noUserAgent) {
				getScanDetailsResponse.data.userAgent = this.lookupData.userAgents.find(ua => ua.isDefault)?.id || 1
			}
			this.$router.push({
				name: SITE_SCANNER_REQUEST_SCAN,
				params: {
					scanTypes: this.lookupData.scanTypes,
					maxUrls: this.lookupData.maxScanUrlResults,
					actionTypes: this.lookupData.actionTypes,
					loginTemplates: this.lookupData.loginTemplates,
					scanDetails: getScanDetailsResponse?.data,
					userAgents: this.lookupData.userAgents
				}
			})
		},
		async handleCloneScheduledScanClick (scheduledScanId) {
			const getScanDetailsResponse = await getScanDetails(null, scheduledScanId)
			this.$router.push({
				name: SITE_SCANNER_REQUEST_SCAN,
				params: {
					scanTypes: this.lookupData.scanTypes,
					maxUrls: this.lookupData.maxScanUrlResults,
					actionTypes: this.lookupData.actionTypes,
					loginTemplates: this.lookupData.loginTemplates,
					scanDetails: getScanDetailsResponse?.data,
					userAgents: this.lookupData.userAgents
				}
			})
		},
		viewReport (scan) {
			if (scan.status === 'Scanning') {
				return
			}
			if (!this.userCanUseSiteScanner) return

			if (scan.status === 'Ready') {
				this.$router.push({
					name: SITE_SCANNER_RESULT,
					query: {
						scanGuid: scan.scanGuid
					},
					params: {
						maxUrls: this.lookupData.maxScanUrlResults
					}
				})
			} else if (scan.status.includes('Unsuccessful')) {
				this.viewErrorScanDetails(scan)
			}
		},
		async downloadReport (scanGuid, downloadType) {
			const response = await downloadScanFile(scanGuid, downloadType, null)
			const fileName = response.headers['content-disposition'].split('filename=')[1].split(';')[0]?.replace(/"/g, '')
			this.generatePdf(response.data, fileName)
		},
		async downloadCsv (scanGuid, downloadType) {
			const response = await downloadScanFile(scanGuid, downloadType, null)
			window.open(response.data.responseFileName)
		},
		handleStatusClick (scan) {
			this.selectedScanStatus = {
				scanName: scan.scanName,
				...scan.statusDetails
			}
			this.scanStatusModalOpen = true
		},
		handleCloseStatusModal () {
			this.scanStatusModalOpen = false
			this.getSiteScans()
		},
		handleCompareClick (scan) {
			this.compareScansSelectModalOpen = true
			this.selectedScanToCompare = scan
			if (scan.groupedSchedulesID == null) {
				this.comparableScans = this.singleScans.filter(item => (item.domain === scan.domain || item.domain.endsWith('.' + scan.domain) || scan.domain.includes(item.domain)) && item.id !== scan.id)
			} else {
				this.comparableScans = this.recurringScans.filter(item => item.groupedSchedulesID === scan.groupedSchedulesID && item.id !== scan.id)
			}
		},
		handleCloseCompareSelectModal () {
			this.compareScansSelectModalOpen = false
			this.selectedScanToCompare = null
			this.comparableScans = null
		},
		viewComparisonResults (scanId) {
			const scanToCompareTo = this.recurringScans?.find(item => item.id === scanId) || this.singleScans.find(item => item.id === scanId)
			this.scanToCompareFrom = JSON.stringify(this.selectedScanToCompare)
			this.scanToCompareTo = JSON.stringify(scanToCompareTo)
			this.compareScansSelectModalOpen = false
			this.compareScansResultsModalOpen = true
		},
		handleCloseCompareResultsModal () {
			if (this.selectedScanToCompare != null) {
				this.compareScansSelectModalOpen = true
			}
			this.compareScansResultsModalOpen = false
			this.scanToCompareFrom = null
			this.scanToCompareTo = null
		},
		async deleteCookieScan () {
			await deleteCookieScan(this.cookieScanToBeRemoved.scanGuid ?? this.cookieScanToBeRemoved.scheduleScanId)
				.then(() => {
					showSnackbar('Cookie Scan Deleted')
					this.cookieScanToBeRemoved = null
					this.getSiteScans()
				})
				.catch(() => {
					showSnackbar({
						text: 'Error Deleting Cookie Scan',
						color: 'red'
					})
					this.cookieScanToBeRemoved = null
				})
		},
		async deleteScheduledCookieScan () {
			await deleteScheduledCookieScan(this.cookieScheduledScanToBeRemoved.scheduleScanId)
				.then(() => {
					showSnackbar('Cookie Scan Deleted')
					this.cookieScheduledScanToBeRemoved = null
					this.getSiteScans()
				})
				.catch(() => {
					showSnackbar({
						text: 'Error Deleting Cookie Scan',
						color: 'red'
					})
					this.cookieScheduledScanToBeRemoved = null
				})
		},
		async cancelRecurringScan () {
			await cancelRecurringScan(this.recurringScanToCancel.groupedSchedulesID)
				.then(() => {
					showSnackbar('Recurring Scan Cancelled')
					this.recurringScanToCancel = null
					this.getSiteScans()
				})
				.catch(() => {
					showSnackbar({
						text: 'Error Cancelling Recurring Scan',
						color: 'red'
					})
					this.recurringScanToCancel = null
				})
		},
		viewErrorScanDetails (scan) {
			this.selectedUnsuccessfulScan = scan
			this.showUnsuccessfulScanModal = true
		},
		closeUnsuccessfulScanModal () {
			this.showUnsuccessfulScanModal = false
			this.selectedUnsuccessfulScan = null
		},
		headingStatusText (scheduleItem) {
			switch (scheduleItem.statusId) {
				case 3:
					return 'Next Scheduled Scan: ' + format(new Date(scheduleItem.dateRequestedUnformatted), DATE_TIME_FORMAT_NO_SECONDS)
				case 4:
					return 'Cancelled'
				default:
					return ''
			}
		},
		hasMultipleDomainEntries (item) {
			return this.singleScanData.filter(x => x.domain === item.domain && x.status === 'Ready').length > 1
		},
		clearFilters () {
			changeSiteScannerDomainSearchQuery(null)
		},
		handleRemovedItemClick (item) {
			if (item.scheduleScanId > 0) {
				this.cookieScheduledScanToBeRemoved = item
			} else {
				this.cookieScanToBeRemoved = item
			}
		},
		filterScans (scans) {
			if (scans != null) {
				return scans.filter(({ scanName, domain, brandId }) => {
					let check = true
					if (siteScannerSearchQuery.value) check = scanName.toLowerCase().includes(siteScannerSearchQuery.value.toLowerCase())
					if (siteScannerDomainSearchQuery.value) check = check && domain.toLowerCase().includes(siteScannerDomainSearchQuery.value.toLowerCase())
					if (selectedAdminPortalBrandId.value !== null) check = check && brandId === selectedAdminPortalBrandId.value
					return check
				})
			} else return scans
		},
		async navigateToNewSiteScanner () {
			if (this.newScannerLoading) return
			this.newScannerLoading = true
			const redirectUrl = await createExchangeTokenRedirect(TARGET_APPS.SITE_SCANNER)
			if (redirectUrl) {
				window.location.href = redirectUrl
			} else {
				this.newScannerLoading = false
			}
		},
		async handleQuickScanRequested () {
			showSnackbar('Quick Scan requested successfully')
			this.showScanTypeModal = false
			await this.getSiteScans()
		}
	}
}
</script>
<style lang="scss">
	.custom-header {
		transition: background-color 0.3s ease-in-out;
	}
	.custom-header.v-expansion-panel-header--active {
		background-color: var(--global-border-color);
	}
	.custom-header:hover {
		background-color: var(--global-border-color);
	}
	.new-scanner-banner {
		background-color: var(--global-card-background, #ffffff) !important;
	}
	.new-scanner-banner .text-subtitle-2,
	.new-scanner-banner .text-caption {
		color: var(--global-foreground, #1f2937);
	}
	.new-scanner-banner::v-deep .v-alert__dismissible {
		position: absolute;
		top: 5px;
		right: 5px;
		margin: 0;
		color: var(--global-cadet-gray) !important;
	}
	.new-scanner-banner::v-deep .v-alert__dismissible::before {
		opacity: 0 !important;
	}
	.new-scanner-banner::v-deep .v-alert__dismissible:hover {
		color: var(--global-foreground, #0B3142) !important;
	}
	.heading-scan-name {
		width: 300px;
	}
	.heading-texts {
		display: flex;
	}
</style>
